import { useRef } from "react";
import { Stethoscope } from "lucide-react";
import { CrudPage } from "../components/crud/CrudPage";
import { Badge } from "../components/ui/Badge";
import { api, mensajeError } from "../api/client";

interface Usuario {
  id: string;
  nombre: string;
  email: string;
  rol: string;
  activo: boolean;
  titulo?: string | null;
  cedula?: string | null;
}

const roles = [
  { value: "ADMIN", label: "Administrador" },
  { value: "VETERINARIO", label: "Veterinario" },
  { value: "RECEPCIONISTA", label: "Recepcionista" },
  { value: "GROOMER", label: "Groomer / Estética" },
  { value: "ENTRENADOR", label: "Entrenador" },
  { value: "PASEADOR", label: "Paseador" },
];

export default function Usuarios() {
  const recargarRef = useRef<(() => void) | null>(null);

  // Se reemplaza el borrado genérico de CrudPage por este flujo propio para
  // poder advertir ANTES de confirmar (no solo dejar que el backend lo
  // rechace después) cuando el usuario que se va a "eliminar" (desactivar)
  // es el único administrador activo del negocio — si eso pasara, nadie
  // podría volver a entrar al panel. El backend también bloquea esto por su
  // cuenta (es la protección real), esto solo mejora el aviso.
  async function eliminarUsuario(row: Usuario) {
    if (row.rol === "ADMIN" && row.activo) {
      try {
        const res = await api.get("/usuarios");
        const otrosAdminsActivos = (res.data.items as Usuario[]).filter(
          (u) => u.id !== row.id && u.rol === "ADMIN" && u.activo
        ).length;
        if (otrosAdminsActivos === 0) {
          alert(
            `No puedes eliminar a ${row.nombre}: es el único administrador activo de tu negocio. Antes de desactivarlo, crea o activa a otro administrador — si no, nadie va a poder entrar al panel.`
          );
          return;
        }
      } catch {
        // Si la verificación previa falla por algo (ej. sin conexión), se
        // deja pasar al flujo normal — el backend de todas formas bloquea el
        // caso real de "quedarse sin administrador" del lado del servidor.
      }
    }

    if (!confirm(`¿Seguro que quieres eliminar a ${row.nombre}? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/usuarios/${row.id}`);
      recargarRef.current?.();
    } catch (err) {
      alert(mensajeError(err));
    }
  }

  return (
    <CrudPage<Usuario>
      resource="/usuarios"
          seccion="usuarios"
      titulo="Usuarios y roles"
          icono={<Stethoscope size={20} />}
      descripcion="El equipo que trabaja en tu negocio y sus permisos."
      nombreSingular="usuario"
      onEliminarPersonalizado={eliminarUsuario}
      recargarRef={recargarRef}
      // Si se deja en blanco (ej. al editar y solo cambiar otro dato), no se
      // debe mandar password:"" al backend — un string vacío no cumple el
      // mínimo de 6 caracteres y el backend lo rechaza con "Datos inválidos"
      // aunque la intención era simplemente no tocar la contraseña.
      transformarAntesDeEnviar={(valores) => {
        const datos = { ...valores };
        if (!datos.password) delete datos.password;
        return datos;
      }}
      campos={[
        { name: "nombre", label: "Nombre completo", required: true },
        { name: "email", label: "Correo", type: "email", required: true },
        { name: "password", label: "Contraseña", type: "text", placeholder: "Solo al crear o para cambiarla" },
        { name: "rol", label: "Rol", type: "select", options: roles, required: true },
        { name: "telefono", label: "Teléfono", type: "tel" },
        { name: "titulo", label: "Título profesional", placeholder: "Ej. MVZ, Dr., Dra." },
        { name: "cedula", label: "Cédula profesional" },
        { name: "especialidad", label: "Especialidad (si es veterinario)" },
      ]}
      columnas={[
        {
          key: "nombre",
          label: "Nombre",
          render: (r) => (r.titulo ? `${r.titulo} ${r.nombre}` : r.nombre),
        },
        { key: "email", label: "Correo" },
        { key: "cedula", label: "Cédula", render: (r) => r.cedula || "—" },
        { key: "rol", label: "Rol", render: (r) => <Badge color="azul">{r.rol}</Badge> },
        { key: "activo", label: "Estado", render: (r) => <Badge color={r.activo ? "verde" : "gris"}>{r.activo ? "Activo" : "Inactivo"}</Badge> },
      ]}
    />
  );
}
